import { useState, useEffect } from 'react'
import { api } from '../../services/api.js'
import { btnSecondary, card, colors, input } from '../../components/ui/index.js'
import { useAuth } from '../../hooks/use-auth.js'

interface Disclosure {
  readonly id: string
  readonly disclosedAt: string
  readonly recipientName: string | null
  readonly disclosureType: string
  readonly purpose: string | null
  readonly description: string | null
}

interface DisclosureViewerProps {
  readonly patientId: string
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8125rem',
}
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.375rem 0.5rem',
  borderBottom: `1px solid ${colors.border}`,
  color: colors.textMuted,
  fontWeight: 500,
  fontSize: '0.75rem',
}
const tdStyle: React.CSSProperties = {
  padding: '0.375rem 0.5rem',
  borderBottom: `1px solid ${colors.border}`,
}

export function DisclosureViewer({ patientId }: DisclosureViewerProps) {
  const { user } = useAuth()
  const isAdmin = user != null && ['admin', 'customer_admin', 'medflow_admin'].includes(user.role)

  const [disclosures, setDisclosures] = useState<Disclosure[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  // Date range filter (client-side)
  const [filterFrom, setFilterFrom] = useState('')
  const [filterTo, setFilterTo] = useState('')

  useEffect(() => {
    let cancelled = false
    api
      .get<Disclosure[]>(`/patients/${patientId}/disclosures`)
      .then((res) => {
        if (cancelled) return
        setDisclosures(res.data ?? [])
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load disclosures')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [patientId])

  const filteredDisclosures = disclosures.filter((d) => {
    const date = d.disclosedAt.slice(0, 10)
    if (filterFrom && date < filterFrom) return false
    if (filterTo && date > filterTo) return false
    return true
  })

  function handleExport() {
    setExporting(true)
    const token = localStorage.getItem('alpha_edge_token')
    const url = `/api/patients/${patientId}/hipaa-export?format=csv`
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `hipaa-disclosure-${patientId.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.csv`
        a.click()
        URL.revokeObjectURL(a.href)
        setExporting(false)
      })
      .catch(() => {
        setError('Failed to export')
        setExporting(false)
      })
  }

  return (
    <div style={{ ...card, marginTop: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', color: colors.text }}>Disclosure History</h3>
        {isAdmin && (
          <button onClick={handleExport} style={btnSecondary} disabled={exporting}>
            {exporting ? 'Exporting...' : 'Export HIPAA Accounting'}
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem' }}>
        <label style={{ fontSize: '0.8125rem', color: colors.textMuted }}>From</label>
        <input
          type="date"
          style={{ ...input, width: '140px' }}
          value={filterFrom}
          onChange={(e) => setFilterFrom(e.target.value)}
        />
        <label style={{ fontSize: '0.8125rem', color: colors.textMuted }}>To</label>
        <input
          type="date"
          style={{ ...input, width: '140px' }}
          value={filterTo}
          onChange={(e) => setFilterTo(e.target.value)}
        />
        {(filterFrom || filterTo) && (
          <button
            type="button"
            style={{ ...btnSecondary, padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
            onClick={() => {
              setFilterFrom('')
              setFilterTo('')
            }}
          >
            Clear
          </button>
        )}
      </div>

      {error && (
        <div
          style={{
            marginBottom: '0.5rem',
            color: colors.danger,
            padding: '0.375rem 0.5rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            fontSize: '0.8125rem',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: colors.textMuted, fontSize: '0.8125rem' }}>Loading...</div>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Recipient</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Purpose</th>
              <th style={thStyle}>Description</th>
            </tr>
          </thead>
          <tbody>
            {filteredDisclosures.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ ...tdStyle, color: colors.textMuted, fontStyle: 'italic' }}>
                  No disclosures
                </td>
              </tr>
            ) : (
              filteredDisclosures.map((d) => (
                <tr key={d.id}>
                  <td style={tdStyle}>{d.disclosedAt.slice(0, 10)}</td>
                  <td style={tdStyle}>{d.recipientName ?? ''}</td>
                  <td style={tdStyle}>{d.disclosureType}</td>
                  <td style={tdStyle}>{d.purpose ?? ''}</td>
                  <td style={tdStyle}>{d.description ?? ''}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}
